import { useState } from 'react';

import axiosClient from '../../api/axios-client';
import { isTwoFactorPending, LoginResponse, TwoFactorPendingResponse } from '../../api/twoFactor/types';
import { forgetTrustedDeviceTokenIfRejected, getTrustedDeviceToken } from '../../helpers/trustedDeviceStorage';

import Button from '../components/Button';
import Input from '../components/Input';
import Text from '../components/Text';
import { t } from '../i18n/langue';
import SecondFacteur from './connexion/SecondFacteur';
import { HOTE_WEB } from './sas/briques';

/**
 * La connexion, dans la PWA uniquement — #21, #79.
 *
 * ## Pourquoi ici et nulle part ailleurs
 *
 * C'est le seul endroit du produit où `m.` propose de se connecter, et c'est ce
 * qui garantit **une session par appareil**. Le sas refuse le formulaire hors
 * standalone parce que le stockage d'une web app installée est séparé de celui de
 * Safari : se connecter dans l'onglet obligerait à recommencer après
 * l'installation.
 *
 * ## Deux culs-de-sac évités, et écrits
 *
 * **Mot de passe oublié.** Le lien part sur `app.` — vers `m.` le jeton de
 * réinitialisation serait avalé par le sas, qui ne montre aucun formulaire. Le
 * parcours sort donc de la PWA et **n'y revient pas seul**. On l'écrit plutôt que
 * de le laisser découvrir : « jamais de cul-de-sac » l'emporte ici sur « ne
 * montrer que ce qui aboutit ».
 *
 * **SSO obligatoire.** `POST /login` refuse le mot de passe en 422 pour un compte
 * couvert par une connexion SSO « enforce ». Sur mobile ce serait un mur muet : le
 * message du serveur est affiché tel quel, et l'écran renvoie vers `app.`, seul
 * endroit qui porte le bouton SSO.
 */

/**
 * Les erreurs de `LoginRequest` arrivent sous `errors.{champ}: string[]`. La clé
 * `credentials` porte à la fois le mauvais mot de passe et le refus SSO — le
 * serveur y met une phrase lisible, on ne la réécrit pas.
 */
function messageDErreur(erreur: unknown): string {
    const reponse = (erreur as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } })
        ?.response;

    const champs = reponse?.data?.errors;
    if (champs) {
        const premier = Object.values(champs)[0];
        if (premier?.[0]) return premier[0];
    }

    if (reponse?.data?.message) return reponse.data.message;

    return t('connexion.erreurReseau');
}

interface ConnexionProps {
    /** Appelé avec le jeton : la coque le pose et se remonte. */
    onConnecte: (token: string) => void;
}

export default function Connexion({ onConnecte }: ConnexionProps) {
    const [email, setEmail] = useState('');
    const [motDePasse, setMotDePasse] = useState('');
    const [erreur, setErreur] = useState<string | null>(null);
    const [enCours, setEnCours] = useState(false);
    // Double authentification : mot de passe accepté, second facteur en attente.
    const [secondFacteur, setSecondFacteur] = useState<TwoFactorPendingResponse | null>(null);

    const soumettre = async () => {
        if (enCours) return;

        setEnCours(true);
        setErreur(null);

        try {
            // Le jeton est nommé `mobile` par le SERVEUR, depuis l'en-tête `Origin`
            // (`AuthController::nomDuJeton`) : c'est lui qui porte la politique de
            // session, et un client capable de la choisir s'accorderait trente jours
            // depuis n'importe quel navigateur. Rien à envoyer d'ici.
            const jetonAppareil = getTrustedDeviceToken(email);
            const { data } = await axiosClient.post<LoginResponse>('/login', {
                email,
                password: motDePasse,
                // Appareil de confiance (2FA) : envoyé seulement s'il existe.
                ...(jetonAppareil ? { trusted_device_token: jetonAppareil } : {}),
            });

            if (isTwoFactorPending(data)) {
                forgetTrustedDeviceTokenIfRejected(email, jetonAppareil, data.two_factor);
                setSecondFacteur(data);
                setEnCours(false);
                return;
            }

            onConnecte(data.token);
        } catch (e) {
            setErreur(messageDErreur(e));
            setEnCours(false);
        }
    };

    return (
        <div className="tf-shell h-full overflow-y-auto bg-white">
            <main className="mx-auto flex min-h-full max-w-md flex-col justify-center px-7 py-10">
                <div className="flex justify-center">
                    <img
                        src="/pwa/icon-192.png"
                        alt=""
                        aria-hidden="true"
                        className="h-16 w-16 rounded-2xl"
                    />
                </div>

                {secondFacteur ? (
                    <div className="mt-8">
                        <SecondFacteur
                            attente={secondFacteur}
                            email={email}
                            onConnecte={onConnecte}
                            onRetour={(message) => {
                                setSecondFacteur(null);
                                setMotDePasse('');
                                setErreur(message);
                            }}
                        />
                    </div>
                ) : (
                <>
                <Text variante="titre" as="h1" className="mt-5 text-center">
                    TriggerFlow
                </Text>

                <form
                    className="mt-8 flex flex-col gap-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void soumettre();
                    }}
                >
                    <Input
                        libelle={t('connexion.adresseEmail')}
                        valeur={email}
                        onChange={setEmail}
                        type="email"
                        inputMode="email"
                        autoComplete="username"
                        disabled={enCours}
                    />

                    <Input
                        libelle={t('connexion.motDePasse')}
                        valeur={motDePasse}
                        onChange={setMotDePasse}
                        type="password"
                        autoComplete="current-password"
                        disabled={enCours}
                    />

                    {erreur && (
                        <div role="alert">
                            <Text variante="petit" ton="danger" className="block leading-snug">
                                {erreur}
                            </Text>
                        </div>
                    )}

                    <Button
                        libelle={enCours ? t('connexion.connexionEnCours') : t('connexion.seConnecter')}
                        type="submit"
                        disabled={enCours || !email || !motDePasse}
                        pleineLargeur
                    />
                </form>

                <Text variante="petit" ton="attenue" className="mt-5 block text-center leading-snug">
                    {t('connexion.resterConnecte')}
                </Text>

                {/*
                    Le parcours de réinitialisation SORT de l'app et n'y revient pas
                    seul : le lien reçu par e-mail viserait `m.`, où le sas
                    l’avalerait. On le dit, plutôt que de laisser quelqu’un
                    l'apprendre en cours de route.
                */}
                <div className="mt-6 border-t border-border-color-light pt-5 text-center">
                    <Text variante="petit" ton="attenue" className="block leading-snug">
                        {t('connexion.motDePasseOublie')}
                    </Text>
                    <a
                        href={`https://${HOTE_WEB}/forgot-password`}
                        className="mt-2 inline-flex min-h-touch items-center text-sm font-medium text-main-color underline underline-offset-2"
                    >
                        {t('connexion.ouvrirHote', { hote: HOTE_WEB })}
                    </a>
                </div>
                </>
                )}
            </main>
        </div>
    );
}
